import { MutationTree } from "vuex";
import {
	UserManagementState,
	UserDetails,
	UserRole,
	Permission,
	Domain,
	DomainGroup,
	UserGroup,
	ContactUser,
	ContactRole, Contact, DomainModal
} from "./types";
import { getDefaultState } from "./state";
import { Group } from "../areas/types";
import { state } from "../event-category-setup";

export const mutations: MutationTree<UserManagementState> = {
	setUserList(state, data: UserDetails[]) {
		state.userList.push(...data);
	},
	resetUserList(state) {
		state.userList = [];
		state.pageIndex = 1;
	},
	setUser(state, data: UserDetails) {
		const exists: number = state.userList.findIndex(x => x.userID == data.userID);
		if (exists >= 0) {
			state.userList[exists] = data;
		} else {
			state.userList.push(data);
		}
	},
	removeUser(state, userId: number) {
		const index: number = state.userList.findIndex(x => x.userID == userId);
		if (index >= 0) {
			state.userList.splice(index, 1);
		}
	},
	updateContactUser(state, { updatedUser, roleIds, groupIds }:
		{ updatedUser: ContactUser, roleIds: number[], groupIds: number[] }
	) {
		const user = state.contacts.find(x => x.userID == updatedUser.userID);
		if (!user) return;
		user.contactRoles = state.contactRoles.find(x => roleIds.includes(x.contactRoleId));
	},
	updateContact(state, updateduser: UserDetails) {
		const user = state.contacts.find(x => x.userID == updateduser.userID);
		if (!user) return;

		user.email = updateduser.email;
		user.name = updateduser.name;
		user.telephone = updateduser.telephone;
		user.groupID = updateduser.groupID;
		user.notes = updateduser.notes;
		user.address = updateduser.address;
		user.isSiteStaff = updateduser.isSiteStaff;
		user.mobile = updateduser.mobile;
		user.receiveIncidentEmails = updateduser.receiveIncidentEmails,
		user.receiveUpdateEmails = updateduser.receiveUpdateEmails
	},
	removeContact(state, userId: number) {
		const uIndex: number = state.contacts.findIndex(x => x.userID == userId);
		if (uIndex >= 0) {
			state.contacts.splice(uIndex, 1);
		}
	},
	setGroupRole(state, { groups, userId }: { groups: number[], userId: number }) {
		const user = state.contacts.find(x => x.userID == userId);
		if (!user) return;
		user.contactRoleGroups = groups;
	},
	setContacts(state, contacts: ContactUser[]) {
		// To ensure we don't get duplicates, we want to ensure our unique contact isn't already in the list.
		contacts.forEach((contact: ContactUser) => {
			if (!state.contacts.find(x => x.userID == contact.userID)) {
				contact.contactRoles = contact.contactRoles ? contact.contactRoles[0] ?? null : null;
				state.contacts.push(contact);
			}
		});
	},
	setUserDetails(state, userDetails: UserDetails[]) {
		state.userList = userDetails;
	},
	setContactRoles(state, ContactRoles: ContactRole[]) {
		state.contactRoles = ContactRoles;
	},
	setPermissions: (state, permissions: Permission[]) => (state.permissions = permissions),
	setUserRoles: (state, userRoles: UserRole[]) => (state.userRoles = userRoles),
	setUserDomains: (state, domains: Domain[]) => (state.userDomains = domains),
	setDomains: (state, domains: Domain[]) => (state.domains = domains),
	setDomain(state, data: Domain) {
		const exists: number = state.domains.findIndex(x => x.domainId == data.domainId && x.groupId == data.groupId);
		if (exists >= 0) {
			state.domains[exists] = data;
		} else {
			state.domains.push(data);
		}
	},
	updateDomain(state, domain: Domain) {
		const index: number = state.domains.findIndex(x => x.domainId == domain.domainId);
		if (index >= 0) {
			state.domains.splice(index, 1);
		}
		state.domains.push(domain);
	},
	removeDomain(state, domainId: number) {
		const index: number = state.domains.findIndex(x => x.domainId == domainId);
		if (index >= 0) {
			state.domains.splice(index, 1);
		}
	},
	setDomainGroups: (state, domainGroups: DomainGroup[]) => (state.domainGroups = domainGroups),
	setDomainGroup(state, data: DomainGroup) {
		const exists: number = state.domainGroups.findIndex(x => x.domainId == data.domainId && x.userGroupId == data.userGroupId);
		if (exists >= 0) {
			state.domainGroups[exists] = data;
		} else {
			state.domainGroups.push(data);
		}
	},
	removeDomainGroup(state, data: DomainGroup) {
		const index: number = state.domainGroups.findIndex(x => x.domainId == data.domainId && x.userGroupId == data.userGroupId);
		if (index >= 0) {
			state.domainGroups.splice(index, 1);
		}
	},
	updateDomainGroup(state, { domainId, userGroupId, domainGroup }: { domainId: number; userGroupId: number; domainGroup: DomainGroup; }) {
		const index: number = state.domainGroups.findIndex(x => x.domainId == domainId && x.userGroupId == userGroupId);
		if (index >= 0) {
			state.domainGroups.splice(index, 1);
		}
		state.domainGroups.push(domainGroup);
	},
	setUserGroups: (state, userGroups: UserGroup[]) => (state.userGroups = userGroups),
	setContact(state, data: ContactUser) {
		const exists: number = state.contacts.findIndex(x => x.userID == data.userID);
		if (exists >= 0) {
			state.contacts[exists] = data;
		} else {
			state.contacts.push(data);
		}

	},
	resetState(state) {
		Object.assign(state, getDefaultState());
	},
	setPageIndex(state: UserManagementState, pageIndex: number) {
		state.pageIndex = pageIndex;
	},
	setAllUsersRetrieved(state: UserManagementState, allRetrieved: boolean) {
		state.allUsersRetrieved = allRetrieved;
	},
	setUserTableIsBusy(state: UserManagementState, isBusy: boolean) {
		state.userTableIsBusy = isBusy;
	},
	setDomainModalShown(state, domainModal: DomainModal) {
		state.domainModal = domainModal;
	},
};